/**
 * properties-panel.tsx — Phase 050 ABox properties panel root.
 *
 * Mounts as the BODY of `InspectorOverlay` when `inspectorOverlayContent
 * === 'abox'` (D-050-03a). Reads:
 *   - `previewSelection` (Phase 048 — which block + propertyKey is selected)
 *   - `currentProject` (Phase 045/049 — source of canonical `__aboxEnvelope.properties`)
 *   - `drafts` (Phase 049 — operator's pending overrides)
 *
 * Renders `<ContentSection>` + `<DesignSection>` when a selection + matching
 * block exist AND `aboxElementMap != null`; renders empty-state guidance
 * otherwise (preserves D-048-03 non-ABox byte-identity — the panel never
 * surfaces editable chrome on the template / openServerProject fast path).
 *
 * Attaches its root DOM via callback-ref to `usePropertiesPanelRefStore.
 * setPanelElement` so `use-preview-selection.ts` (D-050-02) can read the
 * panel element imperatively for the focus-precedence check. Cleans up on
 * unmount via the `useEffect` return so a stale node never blocks the
 * Phase 049.1 `container.focus()` call after the panel closes.
 *
 * Rule-1 safe pattern (replicates `dirty-ring-overlay.tsx:67-80`):
 *   - Subscribe to RAW `drafts` object reference (stable between writes).
 *   - Subscribe to RAW `currentProject` reference.
 *   - Derive `baseline` + `blockDrafts` via `useMemo` — NEVER subscribe to
 *     a selector that returns a fresh object on each call (e.g.,
 *     `s.dirtyBlockIds()` would yield infinite re-render).
 */
import { useCallback, useEffect, useMemo } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { useDraftStore } from '@/features/preview/store/draft-store'
import { usePreviewSelectionStore } from '@/features/preview/store/preview-selection-slice'
import { usePropertiesPanelRefStore } from './properties-panel-ref-store'
import {
  readEnvelope,
  type AboxEnvelope,
} from '@/features/design-system/ontology/abox-render-prep'
import { ContentSection } from './content-section'
import { DesignSection } from './design-section'

export function PropertiesPanel() {
  const previewSelection = usePreviewSelectionStore((s) => s.previewSelection)
  const currentProject = useStudioStore((s) => s.currentProject)
  const drafts = useDraftStore((s) => s.drafts)
  const setPanelElement = usePropertiesPanelRefStore(
    (s) => s.setPanelElement,
  )

  // D-050-02 plumbing — clear the ref on unmount so a focus check after the
  // panel closes never reads a stale node.
  useEffect(() => {
    return () => {
      setPanelElement(null)
    }
  }, [setPanelElement])

  // Callback-ref idiom (Phase 049-01 P1 precedent — we don't need the ref
  // locally, only inside the panel-ref-store; callback-ref is the leanest
  // path here).
  const setRootRef = useCallback(
    (el: HTMLDivElement | null) => {
      setPanelElement(el)
    },
    [setPanelElement],
  )

  // SC#4 — read BASELINE from canonical `__aboxEnvelope.properties` via
  // `readEnvelope`, NOT from the synthesized `ui` tree. Walk
  // `currentProject.blocks` for the block whose `env.id === previewSelection.blockId`.
  const baseline = useMemo<
    { env: AboxEnvelope; blockName: string; blockType: string } | null
  >(() => {
    if (!previewSelection || !currentProject?.blocks) return null
    for (const [name, block] of Object.entries(
      currentProject.blocks as Record<string, unknown>,
    )) {
      const env = readEnvelope(block as never)
      if (env && env.id === previewSelection.blockId) {
        return { env, blockName: name, blockType: env.block_type }
      }
    }
    return null
  }, [previewSelection, currentProject])

  // Rule-1 safe — subscribe to RAW `drafts` map; derive per-block draft via
  // a plain object lookup inside `useMemo`. Phase 049-03's
  // `dirty-ring-overlay.tsx:67-80` precedent.
  const blockDrafts: Record<string, unknown> = useMemo(() => {
    if (!previewSelection) return {}
    return (
      (drafts[previewSelection.blockId] as Record<string, unknown>) ?? {}
    )
  }, [drafts, previewSelection])

  // D-048-03 invariant — non-ABox path renders empty-state only. The
  // editable-layer mount gate (`aboxElementMap != null`) is the same
  // predicate every Phase 048/049/049.1 visible surface uses.
  const aboxElementMap = currentProject?.aboxElementMap
  if (!aboxElementMap || !previewSelection || !baseline) {
    return (
      <div
        ref={setRootRef}
        data-testid="properties-panel-empty"
        className="p-3 text-xs text-muted-foreground"
      >
        Select an element on the Preview canvas to edit its properties.
      </div>
    )
  }

  return (
    <div
      ref={setRootRef}
      data-testid="properties-panel"
      className="flex flex-col"
    >
      <div className="px-3 py-2 border-b border-border bg-muted/30">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-foreground">
          {baseline.blockName}
        </span>
        <span className="text-[10px] text-muted-foreground ml-2">
          {baseline.blockType}
        </span>
        <div className="text-[9px] text-muted-foreground/70 mt-0.5 font-mono">
          {baseline.env.id}
        </div>
      </div>
      <div className="flex flex-col gap-2 p-3">
        <ContentSection baseline={baseline} blockDrafts={blockDrafts} />
        <DesignSection baseline={baseline} blockDrafts={blockDrafts} />
      </div>
    </div>
  )
}
